class EyeRiseMobileNavigation extends HTMLElement {
  connectedCallback() {
    this.button = this.closest('.header')?.querySelector('[data-mobile-menu-button]');
    if (!this.button) return;

    this.button.addEventListener('click', () => this.toggle());
    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.close();
    });
  }

  toggle() {
    if (this.hidden) {
      this.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.handleKeydown);
      return;
    }

    this.close();
  }

  close({ restoreFocus = false } = {}) {
    if (this.hidden) return;

    this.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.handleKeydown);
    if (restoreFocus) this.button.focus();
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape') this.close({ restoreFocus: true });
  };
}

if (!customElements.get('eyerise-mobile-navigation')) {
  customElements.define('eyerise-mobile-navigation', EyeRiseMobileNavigation);
}

class EyeRiseProductGallery extends HTMLElement {
  connectedCallback() {
    this.media = this.querySelector('[data-gallery-main]');
    this.items = [...this.querySelectorAll('[data-gallery-item]')];
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.select(dot)));
    this.media.addEventListener('scroll', this.handleScroll, { passive: true });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        const active = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];
        if (active) this.setActive(active.target);
      }, { root: this.media, threshold: 0.6 });
      this.items.forEach((item) => this.observer.observe(item));
    }
  }

  select(dot) {
    this.setActive(document.getElementById(dot.getAttribute('aria-controls')), { scroll: true });
  }

  setActive(item, { scroll = false } = {}) {
    if (!item) return;
    this.items.forEach((media) => media.classList.toggle('is-active', media === item));
    this.dots.forEach((dot) => dot.setAttribute('aria-current', String(dot.getAttribute('aria-controls') === item.id)));
    if (scroll) item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  handleScroll = () => {
    const center = this.media.getBoundingClientRect().left + (this.media.clientWidth / 2);
    const active = this.items.reduce((closest, item) => {
      const itemCenter = item.getBoundingClientRect().left + (item.clientWidth / 2);
      const closestCenter = closest.getBoundingClientRect().left + (closest.clientWidth / 2);
      return Math.abs(itemCenter - center) < Math.abs(closestCenter - center) ? item : closest;
    });
    this.setActive(active);
  }
}

if (!customElements.get('eyerise-product-gallery')) {
  customElements.define('eyerise-product-gallery', EyeRiseProductGallery);
}

class EyeRiseStickyOffer extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-atc]');
  }

  setActive(panel) {
    this.button ||= this.querySelector('[data-sticky-atc]');
    const primary = panel.querySelector('[data-primary-atc]');
    const form = primary?.closest('form');
    const formId = form?.getAttribute('id');
    if (!this.button) return;

    if (!formId) {
      this.button.disabled = true;
      this.button.removeAttribute('form');
      return;
    }

    this.button.disabled = false;
    this.button.setAttribute('form', formId);
    this.observe(primary);
  }

  observe(primary) {
    window.removeEventListener('scroll', this.updateVisibility);
    this.primary = primary;
    this.updateVisibility();
    window.addEventListener('scroll', this.updateVisibility, { passive: true });
  }

  updateVisibility = () => {
    const visible = this.primary.getBoundingClientRect().bottom < 0;
    if (visible === !this.hidden) return;

    this.hidden = !visible;
    this.dispatchEvent(new CustomEvent('eyerise:sticky-offer-visibility', {
      bubbles: true,
      detail: { visible },
    }));
  };

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateVisibility);
  }
}

if (!customElements.get('eyerise-sticky-offer')) {
  customElements.define('eyerise-sticky-offer', EyeRiseStickyOffer);
}

class EyeRiseEmailOffer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-email-offer-dialog]');
    this.teaser = this.querySelector('[data-email-offer-teaser]');
    this.openButton = this.querySelector('[data-email-offer-open]');
    this.closeButton = this.querySelector('[data-email-offer-close]');
    this.dismissButton = this.querySelector('[data-email-offer-dismiss]');
    if (!this.dialog || !this.teaser || typeof this.dialog.showModal !== 'function') return;

    this.openButton.addEventListener('click', this.open);
    this.closeButton.addEventListener('click', this.close);
    this.dismissButton.addEventListener('click', this.dismissTeaser);
    this.dialog.addEventListener('close', this.showTeaser);
    document.addEventListener('eyerise:sticky-offer-visibility', this.handleStickyVisibility);
    window.addEventListener('resize', this.updateClearance, { passive: true });
    this.resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(this.updateClearance)
      : null;
    this.sticky = document.querySelector('[data-sticky-offer]:not([hidden])');
    if (this.sticky) this.resizeObserver?.observe(this.sticky);
    this.updateClearance();
    this.timer = window.setTimeout(this.open, Number(this.dataset.delay));
  }

  open = () => {
    window.clearTimeout(this.timer);
    this.teaser.hidden = true;
    if (!this.dialog.open) this.dialog.showModal();
  };

  close = () => this.dialog.close();

  showTeaser = () => {
    if (this.teaserDismissed) return;
    this.teaser.hidden = false;
    this.updateClearance();
  };

  dismissTeaser = () => {
    this.teaserDismissed = true;
    this.teaser.hidden = true;
  };

  handleStickyVisibility = ({ target, detail }) => {
    this.resizeObserver?.disconnect();
    this.sticky = detail.visible ? target : null;
    if (this.sticky) this.resizeObserver?.observe(this.sticky);
    this.updateClearance();
  };

  updateClearance = () => {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!isMobile || !this.sticky || this.sticky.hidden) {
      this.style.removeProperty('--email-offer-bottom');
      return;
    }
    const bottom = Math.max(16, window.innerHeight - this.sticky.getBoundingClientRect().top + 8);
    this.style.setProperty('--email-offer-bottom', `${bottom}px`);
  };

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.resizeObserver?.disconnect();
    document.removeEventListener('eyerise:sticky-offer-visibility', this.handleStickyVisibility);
    window.removeEventListener('resize', this.updateClearance);
    this.openButton?.removeEventListener('click', this.open);
    this.closeButton?.removeEventListener('click', this.close);
    this.dismissButton?.removeEventListener('click', this.dismissTeaser);
    this.dialog?.removeEventListener('close', this.showTeaser);
  }
}

if (!customElements.get('eyerise-email-offer')) {
  customElements.define('eyerise-email-offer', EyeRiseEmailOffer);
}

class EyeRiseProductOffer extends HTMLElement {
  connectedCallback() {
    this.choices = [...this.querySelectorAll('[data-offer-choice]')];
    this.panels = [...this.querySelectorAll('[data-offer-panel]')];
    this.sticky = this.querySelector('[data-sticky-offer]');
    this.choices.forEach((choice) => choice.addEventListener('click', () => {
      const handle = choice.dataset.offerChoice;
      this.selectOffer(handle);
      this.panels
        .find((panel) => panel.dataset.offerPanel === handle)
        ?.querySelector(`[data-offer-choice="${handle}"]`)
        ?.focus();
    }));
    this.querySelectorAll('[data-quantity-control]').forEach((control) => {
      control.addEventListener('click', (event) => this.changeQuantity(event));
    });
    this.selectOffer(this.dataset.initialOffer);
  }

  changeQuantity(event) {
    const button = event.target.closest('[data-quantity-action]');
    if (!button || !window.matchMedia('(min-width: 750px)').matches) return;
    const input = button.parentElement.querySelector('input[name="quantity"]');
    const adjustment = button.dataset.quantityAction === 'increase' ? 1 : -1;
    input.value = Math.max(1, Number(input.value) + adjustment);
  }

  selectOffer(handle) {
    this.choices.forEach((choice) => choice.setAttribute('aria-pressed', String(choice.dataset.offerChoice === handle)));
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.offerPanel !== handle;
    });
    const activePanel = this.panels.find((panel) => panel.dataset.offerPanel === handle);
    this.sticky?.setActive(activePanel);
    this.dataset.selectedOffer = handle;
    this.dispatchEvent(new CustomEvent('eyerise:offer-change', { bubbles: true, detail: { handle } }));
  }
}

if (!customElements.get('eyerise-product-offer')) {
  customElements.define('eyerise-product-offer', EyeRiseProductOffer);
}

const disclosureButtons = document.querySelectorAll('[data-disclosure-button]');

disclosureButtons.forEach((button) => {
  const region = document.getElementById(button.getAttribute('aria-controls'));
  if (!region) return;

  region.hidden = true;
  button.setAttribute('aria-expanded', 'false');
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-disclosure-button]');
  if (!button) return;

  const region = document.getElementById(button.getAttribute('aria-controls'));
  if (!region) return;

  const expanded = button.getAttribute('aria-expanded') === 'true';
  button.setAttribute('aria-expanded', String(!expanded));
  region.hidden = expanded;
});
